import React, { ReactElement } from "react";

import { coalesce } from "@kazoohr/helpers";

import { Button } from "../Button";
import { Spacer } from "../Grid";
import { ModalVerticalSpace } from "../ModalVerticalSpace";

import styles from "./ModalActions.scss";

export interface ModalAction {
  onClick: () => void;
  text: string;
  title?: string;
  size?: "small" | "default" | "large";
  loading?: boolean;
  testId?: string;
  disabled?: boolean;
  type?: "submit" | "button";
}

export interface ModalActionProps {
  /**
   * The main action of the modal. Primary-colored button
   */
  mainAction?: ModalAction | null;
  /**
   * The secondary action of the modal. System-outline button
   */
  secondaryAction?: ModalAction | null;
  /**
   * The "cancel" action of the modal. Also often used for form resets. System button
   */
  cancelAction?: ModalAction | null;
  /**
   * Normal actions render all aligned to the left. Use this to add things to the right of the modal actions area
   */
  metaActions?: ReactElement | null;
  /**
   * When rendering actions, you can style the vertical space between the actions and the content by passing a CSS class name here
   */
  verticalSpaceClass?: string;
}

/**
 * The bottom bar of the modal, reserved for the typical modal actions section
 * like "Save | Cancel", and privacy options for meta actions.
 */
export function ModalActions({
  mainAction = null,
  secondaryAction = null,
  cancelAction = null,
  metaActions = null,
  verticalSpaceClass = "",
}: ModalActionProps) {
  if (noActions([mainAction, secondaryAction, cancelAction, metaActions])) {
    return null;
  }

  return (
    <>
      <ModalVerticalSpace className={verticalSpaceClass} />
      <div className={styles["modal-actions"]}>
        <div className={styles["modal-actions-main"]}>
          {mainAction && (
            <>
              <Button
                variant="primary"
                title={coalesce(mainAction.title, mainAction.text)}
                loading={Boolean(mainAction.loading)}
                data-testid={coalesce(mainAction.testId, "modal-main-action")}
                size={coalesce(mainAction.size, "default" as const)}
                onClick={mainAction.onClick}
                disabled={Boolean(mainAction.disabled)}
                type={coalesce(mainAction.type, undefined)}
              >
                {mainAction.text}
              </Button>
              <Spacer orientation="horizontal" />
            </>
          )}
          {secondaryAction && (
            <>
              <Button
                variant="secondary"
                onClick={secondaryAction.onClick}
                title={coalesce(secondaryAction.title, secondaryAction.text)}
                loading={Boolean(secondaryAction.loading)}
                data-testid={coalesce(
                  secondaryAction.testId,
                  "modal-secondary-action"
                )}
                size={coalesce(secondaryAction.size, "default" as const)}
                disabled={Boolean(secondaryAction.disabled)}
                type={coalesce(secondaryAction.type, undefined)}
              >
                {secondaryAction.text}
              </Button>
              <Spacer orientation="horizontal" />
            </>
          )}
          {cancelAction && (
            <>
              <Button
                variant="system"
                onClick={cancelAction.onClick}
                title={coalesce(cancelAction.title, cancelAction.text)}
                loading={Boolean(cancelAction.loading)}
                data-testid={coalesce(
                  cancelAction.testId,
                  "modal-cancel-action"
                )}
                size={coalesce(cancelAction.size, "default" as const)}
                disabled={Boolean(cancelAction.disabled)}
                type={coalesce(cancelAction.type, undefined)}
              >
                {cancelAction.text}
              </Button>
              <Spacer orientation="horizontal" />
            </>
          )}
        </div>
        <div data-testid="modal-meta-action">{metaActions}</div>
      </div>
    </>
  );
}

/**
 * Takes the actions the user might be able to define and returns true if there
 * is nothing to render.
 */
function noActions(actions: ({} | null)[]): boolean {
  for (const action of actions) {
    if (action) {
      return false;
    }
  }
  return true;
}
